"use client";

import React, { useState } from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from "recharts";

// Custom Active Shape for better hover effects
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;

  const sin = Math.sin((-midAngle * Math.PI) / 180);
  const cos = Math.cos((-midAngle * Math.PI) / 180);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 5}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.8}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
        strokeWidth={2}
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
        fontSize={12}
        fontWeight="500"
      >
        {`${payload.name}`}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#666"
        fontSize={12}
      >
        {`${value} (${(percent * 100).toFixed(0)}%)`}
      </text>
    </g>
  );
};

// Custom tooltip for better data display
const CustomTooltip = ({ active, payload, total }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentage = ((data.value / total) * 100).toFixed(1);

    return (
      <div className="bg-white p-3 rounded-lg shadow-md border border-neutral-200">
        <p className="font-medium text-sm mb-1">{data.name}</p>
        <p className="text-sm">
          <span className="font-medium">Candidates:</span> {data.value}
        </p>
        <p className="text-sm">
          <span className="font-medium">Percentage:</span> {percentage}%
        </p>
      </div>
    );
  }
  return null;
};

// Custom legend component
const CustomLegend = (props: any) => {
  const { payload } = props;

  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-3">
      {payload.map((entry: any, index: number) => (
        <li key={`legend-${index}`} className="flex items-center">
          <div
            className="w-3 h-3 rounded-full mr-1.5"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs">
            {entry.value}: {entry.payload.value} (
            {((entry.payload.value / props?.total) * 100).toFixed(0)}%)
          </span>
        </li>
      ))}
    </ul>
  );
};

export function ShortlistedChart({ data = [] }) {
  // Use provided data or fallback to demo data
  const chartData =
    data.length > 0
      ? data
      : [
          {
            name: "LinkedIn",
            value: 1,
            color: "#0A66C2",
          },
          {
            name: "HirePanda",
            value: 2,
            color: "#15a349",
          },
          {
            name: "Indeed",
            value: 1,
            color: "#2557A7",
          },
          {
            name: "Referral",
            value: 1,
            color: "#FF6B00",
          },
        ];

  // Calculate total for percentage display
  const total = chartData.reduce((sum, entry) => sum + entry.value, 0);

  // State to track active index for interactive highlighting
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="text-center mb-1">
        <span className="text-sm font-medium text-neutral-500">
          Total Shortlisted: {total}
        </span>
      </div>
      <ResponsiveContainer height={200} width="100%">
        <PieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={34}
            outerRadius={60}
            paddingAngle={2}
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
            stroke="#fff"
            strokeWidth={1}
          >
            {data.map((entry: { color: string }, index) => (
              <Cell
                key={`slice-${index}`}
                fill={entry.color}
                style={{
                  filter:
                    activeIndex === index
                      ? "drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.3))"
                      : "none",
                  opacity:
                    activeIndex === undefined || activeIndex === index
                      ? 1
                      : 0.7,
                  transition: "opacity 300ms, filter 300ms",
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            content={<CustomLegend />}
            verticalAlign="bottom"
            height={36}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
